import { onCLS, onLCP, onINP, onFCP, Metric } from 'web-vitals';

type Reporter = (name: string, value: number, metric: Metric) => void;

function postMetric(name: string, value: number, metric: Metric) {
  const endpoint = import.meta.env.VITE_WEB_VITALS_ENDPOINT as string | undefined;
  if (!endpoint) return;
  try {
    const body = JSON.stringify({
      name,
      value,
      id: metric.id,
      rating: metric.rating,
      navigationType: performance.getEntriesByType('navigation')?.[0]?.toJSON?.() ?? null,
      userAgent: navigator.userAgent,
      ts: Date.now(),
    });
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = (import.meta.env.VITE_WEB_VITALS_TOKEN as string | undefined)?.trim();
    if (token) headers.Authorization = `Bearer ${token}`;
    if ('sendBeacon' in navigator) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(endpoint, blob);
    } else {
      // intentional
    }
  } catch {
    // no-op
  }
}

function defaultReporter(name: string, value: number, metric: Metric) {
  // В dev не засоряем консоль метриками; отправляем только на endpoint (если настроен)
  postMetric(name, value, metric);
}

function postLongTask(entry: PerformanceEntry) {
  const endpoint = import.meta.env.VITE_WEB_VITALS_ENDPOINT as string | undefined;
  if (!endpoint) return;
  try {
    const body = JSON.stringify({
      name: 'LONG_TASK',
      value: Number(entry.duration?.toFixed?.(1) ?? entry.duration),
      entry: (entry as any).toJSON ? (entry as any).toJSON() : { name: entry.name, startTime: entry.startTime, duration: entry.duration },
      navigationType: performance.getEntriesByType('navigation')?.[0]?.toJSON?.() ?? null,
      userAgent: navigator.userAgent,
      ts: Date.now(),
    });
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = (import.meta.env.VITE_WEB_VITALS_TOKEN as string | undefined)?.trim();
    if (token) headers.Authorization = `Bearer ${token}`;
    if ('sendBeacon' in navigator) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(endpoint, blob);
    } else {
      // intentional
    }
  } catch {
    // no-op
  }
}

export function initWebVitals(report: Reporter = defaultReporter) {
  try {
    onCLS((m) => report('CLS', m.value, m));
    onLCP((m) => report('LCP', m.value, m));
    onINP((m) => report('INP', m.value, m));
    onFCP((m) => report('FCP', m.value, m));
  } catch {
    // no-op
  }

  // Long Tasks (блокировки основного потока)
  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const duration = entry.duration;
        const longTaskThreshold = Number(import.meta.env.VITE_LONG_TASK_WARN_THRESHOLD || 200); // По умолчанию показываем только >200ms
        const enableLongTaskWarnings = import.meta.env.VITE_ENABLE_LONG_TASK_WARNINGS !== 'false'; // Можно отключить через env
        
        // Показываем предупреждение только для критичных задач и если включено
        if (import.meta.env.DEV && enableLongTaskWarnings && duration >= longTaskThreshold) {
          // ✅ ОПТИМИЗАЦИЯ: Более подробная информация о long task для отладки
          const attrs = (entry as any).attribution || [];
          const attribution = attrs.length > 0 ? attrs[0] : null;
          const containerName = attribution?.containerName || 'unknown';
          const containerType = attribution?.containerType || 'unknown';
          const containerSrc = attribution?.containerSrc || 'unknown';
          
          // Собираем stack trace для понимания источника проблемы
          let stackTrace = '';
          try {
            const stack = new Error().stack;
            if (stack) {
              // Берем только первые 5 строк stack trace (исключая сам webVitals.ts)
              const stackLines = stack.split('\n').slice(2, 7).filter(line => !line.includes('webVitals.ts'));
              stackTrace = stackLines.join('\n');
            }
          } catch {
            // Игнорируем ошибки при получении stack trace
          }
          
          const logData: Record<string, any> = {
            duration: `${duration.toFixed(1)}ms`,
            name: entry.name || 'unknown',
            startTime: entry.startTime.toFixed(2),
          };
          
          if (attribution) {
            logData.container = {
              name: containerName,
              type: containerType,
              src: containerSrc,
            };
          }
          
          if (stackTrace) {
            logData.stack = stackTrace;
          }
          
          // ✅ КРИТИЧНО: Для задач >150ms выводим подробную информацию
          if (duration >= 150) {
            console.error('[long-task] ⚠️ КРИТИЧНО!', logData);
            console.group('📊 Подробная информация о long task');
            
            // Основная информация
            console.log('%cDuration:', 'font-weight: bold; color: red;', duration.toFixed(1), 'ms');
            console.log('Start Time:', entry.startTime.toFixed(2), 'ms');
            console.log('Entry Name:', entry.name);
            
            // Attribution информация
            if (attribution) {
              console.group('🎯 Attribution (источник задачи):');
              console.log('Container Type:', attribution.containerType);
              console.log('Container Name:', attribution.containerName || '(пусто)');
              console.log('Container Src:', attribution.containerSrc || '(пусто)');
              console.log('Name:', attribution.name);
              console.log('Full Attribution:', attribution);
              console.groupEnd();
            } else {
              console.warn('⚠️ Attribution недоступен - браузер не смог определить источник');
            }
            
            // Performance marks/measures в момент long task
            try {
              const marksAroundTask = performance.getEntriesByType('mark').filter((mark) => {
                const markTime = mark.startTime;
                const taskStart = entry.startTime;
                const taskEnd = entry.startTime + entry.duration;
                return markTime >= taskStart - 100 && markTime <= taskEnd + 100; // ±100ms вокруг задачи
              });
              
              if (marksAroundTask.length > 0) {
                console.group('📍 Performance Marks рядом с long task (±100ms):');
                marksAroundTask.forEach((mark) => {
                  const offset = (mark.startTime - entry.startTime).toFixed(1);
                  console.log(`${mark.name} (${offset}ms от начала задачи)`);
                });
                console.groupEnd();
              }
            } catch {
              // Игнорируем ошибки при получении marks
            }
            
            // Stack trace (показываем как есть, но объясняем ограничения)
            if (stackTrace) {
              console.group('🔍 Stack Trace (показывает callback, не источник long task):');
              console.log(stackTrace);
              console.groupEnd();
            }
            
            // Информация о React (если доступна)
            if (typeof window !== 'undefined' && (window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__) {
              console.info('ℹ️ React DevTools обнаружен. Используйте Profiler для анализа рендеринга.');
            }
            
            // Информация о текущей странице/компоненте
            try {
              const currentUrl = window.location.pathname;
              const currentHash = window.location.hash;
              console.group('🌐 Контекст:');
              console.log('URL:', currentUrl);
              if (currentHash) console.log('Hash:', currentHash);
              console.log('Timestamp:', new Date().toISOString());
              console.groupEnd();
            } catch {
              // Игнорируем ошибки
            }
            
            console.groupEnd();
          } else {
            // Для задач 100-150ms выводим базовую информацию
            console.warn('[long-task]', `${duration.toFixed(1)}ms`, logData);
          }
        }
        
        // Всегда отправляем на endpoint (если настроен) для мониторинга
        postLongTask(entry);
      }
    });
    observer.observe({ type: 'longtask', buffered: true } as PerformanceObserverInit);
  } catch {
    // Safari/старые браузеры
  }
}


